/**
 * DiscountBadge Component
 *
 * Shared discount code + label badge for all e-commerce templates.
 * Only renders when discountCode is provided.
 * 4 style variants matching the visual identity of each template.
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion';
import { AnimatedText, TextAnimationType } from './AnimatedText';

export type DiscountBadgeVariant = 'gold' | 'green' | 'minimal' | 'mono';

export interface DiscountBadgeProps {
  discountCode?: string;
  discountLabel?: string;
  variant: DiscountBadgeVariant;
  showFrom: number;
  fontFamily: string;
  textAnimation?: TextAnimationType;
}

const VARIANT_STYLES: Record<DiscountBadgeVariant, {
  background: string;
  border: string;
  borderRadius: number;
  labelColor: string;
  codeColor: string;
  shadow: string;
  letterSpacing?: number;
}> = {
  gold: {
    background: 'rgba(0,0,0,0.6)',
    border: '3px dashed #FFD700',
    borderRadius: 12,
    labelColor: 'rgba(255,255,255,0.9)',
    codeColor: '#FFD700',
    shadow: '0 0 20px rgba(255, 215, 0, 0.3)',
  },
  green: {
    background: 'rgba(34, 197, 94, 0.15)',
    border: '2px solid rgba(34, 197, 94, 0.4)',
    borderRadius: 999,
    labelColor: 'rgba(255,255,255,0.9)',
    codeColor: '#22C55E',
    shadow: '0 0 20px rgba(34, 197, 94, 0.2)',
  },
  minimal: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8,
    labelColor: 'rgba(255,255,255,0.7)',
    codeColor: '#ffffff',
    shadow: 'none',
    letterSpacing: 2,
  },
  mono: {
    background: '#ffffff',
    border: 'none',
    borderRadius: 8,
    labelColor: '#333333',
    codeColor: '#000000',
    shadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
};

export const DiscountBadge: React.FC<DiscountBadgeProps> = ({
  discountCode,
  discountLabel,
  variant,
  showFrom,
  fontFamily,
  textAnimation = 'fade_scale',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  if (!discountCode) return null;

  const style = VARIANT_STYLES[variant];

  const opacity = interpolate(
    frame,
    [showFrom, showFrom + fps * 0.4],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  const translateY = interpolate(
    frame,
    [showFrom, showFrom + fps * 0.4],
    [15, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Shake for gold variant
  const shake = variant === 'gold' && frame > showFrom + fps * 0.4
    ? Math.sin((frame - showFrom) * 0.5) * 2
    : 0;

  return (
    <div
      style={{
        opacity,
        transform: `translateY(${translateY}px) translateX(${shake}px)`,
        backgroundColor: style.background,
        border: style.border,
        borderRadius: style.borderRadius,
        padding: '14px 40px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        boxShadow: style.shadow,
      }}
    >
      {discountLabel && (
        <AnimatedText
          text={discountLabel}
          animationType={textAnimation}
          startFrame={showFrom}
          color={style.labelColor}
          fontSize={28}
          fontWeight={600}
          fontFamily={fontFamily}
          textAlign="center"
          wordDelay={3}
        />
      )}
      <div style={{ letterSpacing: style.letterSpacing }}>
        <AnimatedText
          text={discountCode}
          animationType={textAnimation}
          startFrame={showFrom + (discountLabel ? fps * 0.15 : 0)}
          color={style.codeColor}
          fontSize={36}
          fontWeight={700}
          fontFamily={fontFamily}
          textAlign="center"
          wordDelay={4}
        />
      </div>
    </div>
  );
};
